import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Info, Check, Minus, Loader2, Sparkles } from 'lucide-react';
import { useUserTier, useTrialStatus } from '../hooks/useUserTier';
import { useAuthStore } from '../store/useAuthStore';
import { TIER_LIMITS, type UserTier } from '../lib/capabilities';
import { supabase } from '../lib/supabase';
import { showToast } from '../utils/toast';
import { trackEvent } from '../lib/analytics';
import { useTranslation } from 'react-i18next';
import { activateTrial } from '../services/profile.service';

// ─── Types & constants ──────────────────────────────────────────────────────

type PlanTier = 'FREE' | 'PRO' | 'MAX' | 'BUSINESS';

const TIER_ORDER: PlanTier[] = ['FREE', 'PRO', 'MAX', 'BUSINESS'];

interface PlanCard {
  tier: PlanTier;
  name: string;
  tagline: string;
  features: string[];
  recommended?: boolean;
  comingSoon?: boolean;
  badge?: { label: string; color: string };
}

const PLAN_CARDS: PlanCard[] = [
  {
    tier: 'FREE',
    name: 'Free',
    tagline: 'subscription.plan_free_tagline',
    features: [
      'subscription.plan_free_brain',
      'subscription.plan_free_elements',
      'subscription.plan_free_credits',
      'subscription.plan_free_trial',
    ],
  },
  {
    tier: 'PRO',
    name: 'Pro',
    tagline: 'subscription.plan_pro_tagline',
    features: [
      'subscription.plan_pro_brains',
      'subscription.plan_pro_elements',
      'subscription.plan_pro_credits',
      'subscription.plan_pro_synapse',
    ],
    recommended: true,
  },
  {
    tier: 'MAX',
    name: 'Max',
    tagline: 'subscription.plan_max_tagline',
    features: [
      'subscription.plan_max_brains',
      'subscription.plan_max_elements',
      'subscription.plan_max_credits',
      'subscription.plan_max_views',
    ],
    badge: { label: 'subscription.plan_max_badge_label', color: 'purple' },
  },
  {
    tier: 'BUSINESS',
    name: 'Business',
    tagline: 'subscription.plan_business_tagline',
    features: [
      'subscription.plan_business_includes_max',
      'subscription.plan_business_multi_users',
      'subscription.plan_business_collab',
    ],
    comingSoon: true,
  },
];

const REASON_MESSAGES: Record<string, string> = {
  node_limit: 'subscription.reason_node_limit',
  ai_credits: 'subscription.reason_ai_credits',
  brain_limit: 'subscription.reason_brain_limit',
};

interface ComparisonRow {
  label: string;
  values: Record<PlanTier, string | boolean>;
}

const COMPARISON_ROWS: ComparisonRow[] = [
  {
    label: 'subscription.compare_brains',
    values: {
      FREE: '1',
      PRO: '3-5',
      MAX: 'subscription.unlimited',
      BUSINESS: 'subscription.unlimited',
    },
  },
  {
    label: 'subscription.compare_elements',
    values: {
      FREE: '~150',
      PRO: '500',
      MAX: 'subscription.unlimited',
      BUSINESS: 'subscription.unlimited',
    },
  },
  {
    label: 'subscription.compare_ai_credits',
    values: { FREE: '50', PRO: '300', MAX: '2 000', BUSINESS: 'subscription.unlimited' },
  },
  {
    label: 'subscription.compare_synapse',
    values: { FREE: true, PRO: true, MAX: true, BUSINESS: true },
  },
  {
    label: 'subscription.compare_entity_types',
    values: { FREE: '5', PRO: '5', MAX: '5', BUSINESS: '5' },
  },
  {
    label: 'subscription.compare_views',
    values: {
      FREE: 'subscription.all_views',
      PRO: 'subscription.all_views',
      MAX: 'subscription.all_views',
      BUSINESS: 'subscription.all_views',
    },
  },
  {
    label: 'subscription.compare_attachments',
    values: { FREE: false, PRO: true, MAX: true, BUSINESS: true },
  },
  {
    label: 'subscription.compare_multi_users',
    values: { FREE: false, PRO: false, MAX: false, BUSINESS: true },
  },
  {
    label: 'subscription.compare_priority_support',
    values: { FREE: false, PRO: false, MAX: true, BUSINESS: true },
  },
];

// ─── Page ────────────────────────────────────────────────────────────────────

export function AbonnementPage() {
  const { t } = useTranslation();
  const currentTier = useUserTier();
  const { hasNeverHadTrial } = useTrialStatus();
  const session = useAuthStore((state) => state.session);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [checkoutLoadingPlan, setCheckoutLoadingPlan] = useState<string | null>(null);
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  const [isActivatingTrial, setIsActivatingTrial] = useState(false);
  const hasShownReturnToastRef = useRef(false);

  const reason = searchParams.get('reason');
  const required = searchParams.get('required') as PlanTier | null;

  const currentTierIndex = TIER_ORDER.indexOf(currentTier as PlanTier);

  // Gestion du retour Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true' && !hasShownReturnToastRef.current) {
      hasShownReturnToastRef.current = true;
      window.history.replaceState({}, '', '/abonnement');
      showToast(t('subscription.toast_activated'), 'success');
      trackEvent('upgrade_complete');
    }
    if (params.get('canceled') === 'true' && !hasShownReturnToastRef.current) {
      hasShownReturnToastRef.current = true;
      window.history.replaceState({}, '', '/abonnement');
      showToast(t('subscription.toast_cancelled'), 'info');
    }
  }, []);

  const handlePortal = async () => {
    setIsPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('stripe-portal');
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error('Erreur portal:', err);
      showToast(t('subscription.toast_manage_error'), 'error');
    } finally {
      setIsPortalLoading(false);
    }
  };

  const handleCheckout = async (priceKey: 'pro' | 'max') => {
    setCheckoutLoadingPlan(priceKey);
    trackEvent('checkout_start', { plan: priceKey });
    try {
      const {
        data: { session: authSession },
      } = await supabase.auth.getSession();
      if (!authSession) {
        navigate('/login');
        return;
      }

      const { data, error } = await supabase.functions.invoke('stripe-checkout', {
        body: { priceKey },
      });

      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error('Erreur checkout:', err);
      showToast(t('subscription.toast_payment_error'), 'error');
    } finally {
      setCheckoutLoadingPlan(null);
    }
  };

  const handleActivateTrial = async () => {
    if (!session?.user?.id) return;
    setIsActivatingTrial(true);
    try {
      await activateTrial(session.user.id);
      trackEvent('trial_activated');
      showToast(t('subscription.trial_toast_success'), 'success');
      navigate(0);
    } catch (err) {
      console.error('Erreur activation trial:', err);
      showToast(t('subscription.toast_payment_error'), 'error');
      setIsActivatingTrial(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-6xl mx-auto px-4 py-10 sm:px-6 lg:px-8">
        {/* ── Contextual banner ── */}
        {reason && REASON_MESSAGES[reason] && (
          <div className="mb-8 flex items-start gap-3 rounded-lg border border-blue-500/40 bg-blue-900/30 px-5 py-4">
            <Info className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
            <p className="text-sm text-blue-200">{t(REASON_MESSAGES[reason])}</p>
          </div>
        )}

        {/* ── Active subscription banner ── */}
        {currentTier !== 'FREE' && currentTier !== 'VISITOR' && (
          <div className="mb-8 p-5 rounded-xl bg-zinc-800/60 border border-zinc-700/50 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-zinc-200">
                {t('subscription.current_plan', { tier: currentTier })}
              </p>
              <p className="text-xs text-zinc-500 mt-1">{t('subscription.manage_subtitle')}</p>
            </div>
            <button
              onClick={handlePortal}
              disabled={isPortalLoading}
              className="shrink-0 px-4 py-2 text-sm font-medium bg-zinc-700 hover:bg-zinc-600 text-zinc-200 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isPortalLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              {isPortalLoading ? t('subscription.loading') : t('subscription.manage_button')}
            </button>
          </div>
        )}

        {/* ── Trial CTA ── */}
        {currentTier === 'FREE' && hasNeverHadTrial && (
          <div className="mb-8 rounded-xl border border-violet-500/30 bg-violet-950/20 px-5 py-5">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-violet-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-zinc-100">
                    {t('subscription.trial_cta_title')}
                  </p>
                  <p className="text-xs text-zinc-400 mt-0.5">
                    {t('subscription.trial_cta_subtitle')}
                  </p>
                </div>
              </div>
              <button
                onClick={handleActivateTrial}
                disabled={isActivatingTrial}
                className="shrink-0 px-5 py-2.5 rounded-lg text-sm font-medium bg-violet-600 hover:bg-violet-500 text-white transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isActivatingTrial && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('subscription.trial_cta_button')}
              </button>
            </div>
          </div>
        )}

        {/* ── Header ── */}
        <div className="text-center mb-10">
          <h1 className="text-2xl sm:text-3xl font-bold text-zinc-100">
            {t('subscription.choose_plan')}
          </h1>
          <p className="mt-2 text-zinc-400">{t('subscription.subtitle')}</p>
        </div>

        {/* ── Cards grid ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
          {PLAN_CARDS.map((card) => {
            const limits = TIER_LIMITS[card.tier];
            const isCurrent = card.tier === currentTier;
            const tierIdx = TIER_ORDER.indexOf(card.tier);
            const isLower = tierIdx < currentTierIndex;
            const isHighlighted = required === card.tier;

            return (
              <div
                key={card.tier}
                className={`relative flex flex-col rounded-xl border p-6 transition-colors ${
                  isCurrent
                    ? 'border-blue-500 bg-zinc-800/80'
                    : isHighlighted
                      ? 'border-blue-400/60 bg-zinc-800/60 ring-1 ring-blue-400/30'
                      : card.recommended
                        ? 'border-zinc-600 bg-zinc-800/60'
                        : 'border-zinc-700 bg-zinc-800/40'
                }`}
              >
                {/* Badges */}
                <div className="flex items-center gap-2 mb-4">
                  {isCurrent && (
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full">
                      {t('subscription.plan_current_badge')}
                    </span>
                  )}
                  {card.recommended && !isCurrent && (
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-violet-500/20 text-violet-400 px-2 py-0.5 rounded-full">
                      {t('subscription.recommended_badge')}
                    </span>
                  )}
                  {card.badge && !isCurrent && (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400">
                      {t(card.badge.label)}
                    </span>
                  )}
                  {card.comingSoon && (
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-zinc-600/40 text-zinc-400 px-2 py-0.5 rounded-full">
                      {t('subscription.coming_soon')}
                    </span>
                  )}
                </div>

                {/* Tier name & price */}
                <h3 className="text-lg font-semibold text-zinc-100">{card.name}</h3>
                <div className="mt-1 mb-1">
                  {limits.price === 0 ? (
                    <span className="text-2xl font-bold text-zinc-100">
                      {t('subscription.free_price_label')}
                    </span>
                  ) : (
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl font-bold text-zinc-100">
                        {card.tier === 'BUSINESS' ? '~20€' : `${limits.price}€`}
                      </span>
                      <span className="text-sm text-zinc-400">
                        {card.tier === 'BUSINESS'
                          ? t('subscription.per_user_month')
                          : t('subscription.per_month')}
                      </span>
                    </div>
                  )}
                </div>
                <p className="text-sm text-zinc-400 mb-5">{t(card.tagline)}</p>

                {/* Features */}
                <ul className="space-y-2 mb-6 flex-1">
                  {card.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                      <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                      {t(f)}
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                {isCurrent ? (
                  <button
                    disabled
                    className="w-full py-2.5 rounded-lg text-sm font-medium bg-zinc-700 text-zinc-400 cursor-not-allowed"
                  >
                    {t('subscription.plan_current_badge')}
                  </button>
                ) : card.tier === 'BUSINESS' ? (
                  <div>
                    <button
                      onClick={() => {
                        window.location.href =
                          'mailto:contact@visualbrain.io?subject=Int%C3%A9r%C3%AAt%20plan%20Business%20Visual%20Brain';
                        setTimeout(() => {
                          navigator.clipboard.writeText('contact@visualbrain.io').then(() => {
                            showToast(t('subscription.toast_email_copied'), 'info');
                          });
                        }, 500);
                      }}
                      title="contact@visualbrain.io"
                      className="w-full py-2.5 rounded-lg text-sm font-medium bg-zinc-700 text-zinc-200 hover:bg-zinc-600 transition-colors text-center"
                    >
                      {t('subscription.contact_us')}
                    </button>
                    <p className="text-[11px] text-zinc-500 text-center mt-1.5">
                      contact@visualbrain.io
                    </p>
                  </div>
                ) : isLower ? (
                  <button
                    disabled
                    className="w-full py-2.5 rounded-lg text-sm font-medium bg-zinc-700 text-zinc-500 cursor-not-allowed"
                  >
                    {t('subscription.choose_plan_cta')}
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      if (card.tier === 'PRO') handleCheckout('pro');
                      else if (card.tier === 'MAX') handleCheckout('max');
                    }}
                    disabled={checkoutLoadingPlan !== null}
                    className="w-full py-2.5 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  >
                    {checkoutLoadingPlan === (card.tier === 'PRO' ? 'pro' : 'max') && (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    )}
                    {checkoutLoadingPlan === (card.tier === 'PRO' ? 'pro' : 'max')
                      ? t('subscription.redirecting')
                      : currentTier !== 'FREE' && currentTier !== 'VISITOR'
                        ? t('subscription.upgrade')
                        : t('subscription.choose_plan_cta')}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Comparison table ── */}
        <div className="mb-10">
          <h2 className="text-lg font-semibold text-zinc-100 mb-5">
            {t('subscription.comparison_title')}
          </h2>
          <div className="overflow-x-auto rounded-xl border border-zinc-700">
            <table className="w-full min-w-[600px] text-sm">
              <thead>
                <tr className="border-b border-zinc-700 bg-zinc-800/60">
                  <th className="text-left px-4 py-3 font-medium text-zinc-400">
                    {t('subscription.comparison_fonctionnalite')}
                  </th>
                  {PLAN_CARDS.map((c) => (
                    <th
                      key={c.tier}
                      className={`px-4 py-3 font-medium text-center ${
                        c.tier === currentTier ? 'text-blue-400' : 'text-zinc-300'
                      }`}
                    >
                      {c.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map((row, i) => (
                  <tr
                    key={row.label}
                    className={`border-b border-zinc-700/50 ${i % 2 === 0 ? 'bg-zinc-900/30' : ''}`}
                  >
                    <td className="px-4 py-3 text-zinc-300">{t(row.label)}</td>
                    {PLAN_CARDS.map((c) => {
                      const v = row.values[c.tier];
                      return (
                        <td key={c.tier} className="px-4 py-3 text-center">
                          {v === true ? (
                            <Check className="w-4 h-4 text-emerald-400 mx-auto" />
                          ) : v === false ? (
                            <Minus className="w-4 h-4 text-zinc-500 mx-auto" />
                          ) : (
                            <span className="text-zinc-200">
                              {typeof v === 'string' && v.startsWith('subscription.') ? t(v) : v}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
